// @ts-check
import { BASELINE_CONTROL_GROUPS } from '../constants/controlGroups.js';
import { buildDefaultVendorAssessmentConfig } from '../constants/vendorAssessmentDefaults.js';

/**
 * The seed state for a brand-new tenant, and the value-level source of truth for
 * the tenant state blob. The `StateShape` annotation binds it to
 * `src/state/types.js` — adding a key here without adding it there (or vice
 * versa) fails `npm run typecheck`.
 *
 * @type {import('./types.js').StateShape}
 */
const INITIAL_STATE = {
  documents: [
    {
      id: 'doc1',
      title: 'Information Security Policy',
      version: '2.1',
      // Legacy field kept for backward compat; ownerId is canonical.
      owner: 'user-admin',
      // ── New workflow fields ─────────────────────────────────────────────
      ownerId: 'user-admin', // formal approver — must differ from writerId
      writerId: 'user-manager', // responsible editor
      // Reviewer list for current/next cycle
      reviewers: [],
      reviewDecisions: [],
      reviewerEscalations: [],
      reviewerOverride: null,
      reviewCycleStartedAt: null,
      reviewerEscalationDays: 7,
      // Approval record (version-scoped, immutable once set)
      approvalRecord: {
        version: '2.1',
        approvedBy: 'user-admin',
        approvedAt: '2024-12-01T10:00:00Z',
        comment: null,
      },
      approvalRequired: true,
      approvalRequiredLockedAt: null,
      requiredReviewerCount: null,
      // Publication
      publishedAt: '2024-12-01T10:00:00Z',
      publishedBy: 'user-admin',
      effectiveFrom: '2024-12-01T10:00:00Z',
      // Minor amendments (published only)
      minorAmendments: [],
      // Expiry / obsolescence
      expiresAt: null,
      renewalTaskId: null,
      obsoleteAt: null,
      obsoletedBy: null,
      supersededBy: null,
      // Blocking
      ownerAbsenceTaskId: null,
      ownerReassignmentLog: [],
      // Immutable audit trail
      stateHistory: [
        {
          from: null,
          to: 'published',
          at: '2024-12-01T10:00:00Z',
          by: 'user-admin',
          comment: null,
          reason: 'initial-publish',
        },
      ],
      // ── Existing fields ─────────────────────────────────────────────────
      lastUpdated: '2024-12-01T10:00:00Z',
      nextReview: '2026-06-01T10:00:00Z',
      slaDays: 180,
      controls: ['SCF-GOV-001', 'SCF-GOV-004', 'SCF-PPL-002', 'SCF-AUD-002'],
      description:
        'Master information security policy covering roles, responsibilities, and security objectives.',
      category: 'Policy',
      status: 'compliant',
      documentStatus: 'published',
      // Legacy approval fields — kept for backward compat, superseded by approvalRecord
      approverId: null,
      approvalRequestedAt: null,
      approvalComment: null,
      approvalDecision: null,
      approvalDecidedAt: null,
      requiredReaders: ['user-admin'],
      readAcknowledgements: [
        { user: 'user-admin', readAt: '2024-12-05T09:00:00Z', version: '2.1' },
      ],
      versionHistory: [
        {
          version: '2.0',
          updatedAt: '2024-06-01T10:00:00Z',
          updatedBy: 'user-admin',
          changes: ['Initial v2.0 release', 'Aligned to ISO 27001:2022'],
        },
        {
          version: '2.1',
          updatedAt: '2024-12-01T10:00:00Z',
          updatedBy: 'user-admin',
          changes: ['Controls added: SCF-GOV-004, SCF-AUD-002', 'Description updated'],
          approvalRecord: {
            version: '2.1',
            approvedBy: 'user-admin',
            approvedAt: '2024-12-01T10:00:00Z',
            comment: null,
          },
          reviewSkipped: true,
          reviewSkipJustification: 'Initial release',
        },
      ],
    },
    {
      id: 'doc2',
      title: 'Access Control Procedure',
      version: '1.4',
      owner: 'user-admin',
      ownerId: 'user-admin',
      writerId: 'user-manager',
      reviewers: [
        {
          userId: 'user-manager',
          required: true,
          assignedAt: '2025-03-15T10:00:00Z',
          assignedBy: 'user-manager',
        },
      ],
      reviewDecisions: [],
      reviewerEscalations: [],
      reviewerOverride: null,
      reviewCycleStartedAt: '2025-03-15T10:00:00Z',
      reviewerEscalationDays: 7,
      approvalRecord: null,
      approvalRequired: true,
      approvalRequiredLockedAt: '2025-03-15T10:00:00Z',
      requiredReviewerCount: null,
      publishedAt: null,
      publishedBy: null,
      effectiveFrom: null,
      minorAmendments: [],
      expiresAt: null,
      renewalTaskId: null,
      obsoleteAt: null,
      obsoletedBy: null,
      supersededBy: null,
      ownerAbsenceTaskId: null,
      ownerReassignmentLog: [],
      stateHistory: [
        {
          from: 'draft',
          to: 'in_review',
          at: '2025-03-15T10:00:00Z',
          by: 'user-manager',
          comment: null,
          reason: null,
        },
      ],
      lastUpdated: '2024-10-15T10:00:00Z',
      nextReview: '2025-04-15T10:00:00Z',
      slaDays: 180,
      controls: ['SCF-IAM-001', 'SCF-IAM-002', 'SCF-IAM-003', 'SCF-IAM-004', 'SCF-IAM-008'],
      description:
        'Procedures for granting, reviewing, and revoking access rights across all systems.',
      category: 'Procedure',
      status: 'partial',
      documentStatus: 'in_review',
      approverId: 'user-manager',
      approvalRequestedAt: '2025-03-15T10:00:00Z',
      approvalComment: null,
      approvalDecision: null,
      approvalDecidedAt: null,
      requiredReaders: ['user-admin'],
      readAcknowledgements: [
        { user: 'user-admin', readAt: '2024-10-20T10:00:00Z', version: '1.4' },
      ],
      versionHistory: [
        {
          version: '1.3',
          updatedAt: '2024-04-10T10:00:00Z',
          updatedBy: 'user-admin',
          changes: ['TCF access controls added: SCF-IAM-001, SCF-IAM-002'],
        },
        {
          version: '1.4',
          updatedAt: '2024-10-15T10:00:00Z',
          updatedBy: 'user-admin',
          changes: [
            'Controls added: SCF-IAM-003, SCF-IAM-004',
            'Status changed: compliant → partial',
          ],
        },
      ],
    },
    {
      id: 'doc3',
      title: 'Incident Response Plan',
      version: '3.0',
      owner: 'user-admin',
      ownerId: 'user-admin',
      writerId: 'user-manager',
      reviewers: [],
      reviewDecisions: [],
      reviewerEscalations: [],
      reviewerOverride: null,
      reviewCycleStartedAt: null,
      reviewerEscalationDays: 7,
      approvalRecord: {
        version: '3.0',
        approvedBy: 'user-admin',
        approvedAt: '2024-11-20T10:00:00Z',
        comment: null,
      },
      approvalRequired: true,
      approvalRequiredLockedAt: null,
      requiredReviewerCount: null,
      publishedAt: '2024-11-20T10:00:00Z',
      publishedBy: 'user-admin',
      effectiveFrom: '2024-11-20T10:00:00Z',
      minorAmendments: [],
      expiresAt: null,
      renewalTaskId: null,
      obsoleteAt: null,
      obsoletedBy: null,
      supersededBy: null,
      ownerAbsenceTaskId: null,
      ownerReassignmentLog: [],
      stateHistory: [
        {
          from: null,
          to: 'published',
          at: '2024-11-20T10:00:00Z',
          by: 'user-admin',
          comment: null,
          reason: 'initial-publish',
        },
      ],
      lastUpdated: '2024-11-20T10:00:00Z',
      nextReview: '2026-05-20T10:00:00Z',
      slaDays: 180,
      controls: ['SCF-INC-001', 'SCF-INC-002', 'SCF-INC-003', 'SCF-INC-004', 'SCF-INC-005'],
      description:
        'Incident detection, classification, response, and post-incident review procedures.',
      category: 'Plan',
      status: 'compliant',
      documentStatus: 'published',
      approverId: null,
      approvalRequestedAt: null,
      approvalComment: null,
      approvalDecision: null,
      approvalDecidedAt: null,
      requiredReaders: ['user-admin'],
      readAcknowledgements: [
        { user: 'user-admin', readAt: '2024-11-25T09:00:00Z', version: '3.0' },
      ],
      versionHistory: [
        {
          version: '3.0',
          updatedAt: '2024-11-20T10:00:00Z',
          updatedBy: 'user-admin',
          changes: ['Major rewrite for ISO 27001:2022 alignment', 'TCF incident controls mapped'],
          approvalRecord: {
            version: '3.0',
            approvedBy: 'user-admin',
            approvedAt: '2024-11-20T10:00:00Z',
            comment: null,
          },
          reviewSkipped: true,
          reviewSkipJustification: 'Major version — owner-approved',
        },
      ],
    },
  ],
  // DEPRECATED as a display/consumption source — the Tasks page reads
  // adhocTasks/enabledObligations/risk.actions[] instead. Still a live
  // staging key: Gap Analysis ("Generate Tasks", src/utils/gapToTasks.js)
  // still writes new rows here, and migrateState() (scfMigration.js, runs
  // on every load) still reads and drains it via obligationMigration.js's
  // migrateAdhocTasks/migrateTaskEvidence/migrateRiskActions. Do not remove
  // without redirecting the gapToTasks.js write path first.
  tasks: [],
  // Evidence register — append-only ledger of post-event conclusions.
  // Legacy rows may be plain { id, label, ... }. Governance rows added by the
  // risk module carry the shape documented in src/state/risk/types.js:
  //   { id, tenantId, scope: { kind: 'risk', id: riskId }, type: EvidenceType,
  //     assertedAt, sourceSystemId, provenance: { actorId?, actorRole?, clientId? },
  //     payload, linkedAssertionId?, linkedDecisionId?, linkedObligationId?,
  //     supersedesEvidenceId?, sweeperWindowIndex? }
  // Rows are immutable — corrections are new rows linked via supersedesEvidenceId.
  evidence: [],

  // DEPRECATED — replaced by enabledObligations[]. Retained for migration compatibility.
  adoptedStandardTasks: [],

  // ── Enabled Obligations ──────────────────────────────────────────
  // ObligationDefinition IDs enabled for this tenant.
  // Shape: { obligationId, enabledAt, enabledBy, cadenceOverride?, assigneeId?, enabled }
  enabledObligations: [],
  // Thin UI cache (assignment, snooze) — non-authoritative
  obligationProjections: [],
  // Statement of Applicability decisions — map of obligationId → decision record.
  // Shape: { [obligationId]: { decision: 'in_scope'|'out_of_scope'|'undecided',
  //   reason: string|null, decidedAt: ISO|null, decidedBy: userId|null } }
  // Automatically set to in_scope when a tenant enables an obligation.
  // out_of_scope requires a mandatory reason — forms the audit trail for ISO 27001 clause 6.1.3.
  obligationDecisions: {},
  // Residual human attestations for manual-strategy obligations — the single
  // off-system fact ("this activity actually happened") that a published
  // document alone can never prove. Anchored to controlIds (OBL-B-005), with
  // obligationId carried for cadence/lookup only.
  // Shape: { id, obligationId, controlIds, note, attestedBy, attestedByName, attestedAt }
  obligationAttestations: [],
  // Standalone evidence/artefact register
  artefacts: [],
  // Artefact module configuration
  // verificationIntervalDays: how long before an external-URL artefact is marked
  // unverified by the daily sweeper (artefact-verification-sweeper). Default 90.
  artefactSettings: { verificationIntervalDays: 90 },
  // Manual/transitional tasks (Tier 3)
  adhocTasks: [],

  risks: [],
  riskSettings: null,

  // Cost-of-Compliance analytics (read-only derivation over risk-resolution
  // cost fields). `displayCurrency` is the single tenant display currency —
  // figures in other currencies are surfaced separately, never converted
  // (no FX feeds in v1). `postureTarget` is an optional target posture score.
  // Consumed by the Cost of Compliance executive dashboard widget and the
  // weekly compliance digest. Additive/optional — undefined on existing tenants.
  costSettings: { displayCurrency: 'EUR', postureTarget: null },

  // ── Risk governance ledger (Phase 2a — slots only, no consumers yet) ─
  // Append-only governance artifacts for the Risk module redesign. Shapes
  // are defined as JSDoc typedefs in src/state/risk/types.js. These three
  // arrays are read-only for the rest of the app in Phase 2a; Phase 2b
  // introduces the risks-write actions that populate them.
  //
  // riskAssertions: impact/likelihood/residual claims about a risk.
  //   { id, riskId, kind: 'assessment'|'residual-estimate'|'scope'|'context',
  //     assertedBy, assertedByRole, assertedAt,
  //     values: { impact?, likelihood?, rationale },
  //     supersedesAssertionId? }
  riskAssertions: [],
  // riskDecisions: explicit, TTL-bounded governance decisions.
  //   { id, riskId, type: 'mitigate'|'accept'|'transfer',
  //     assertedBy, assertedByRole, assertedAt, ttlDays,
  //     basedOnAssertionId, expectedResidualAssertionId?,
  //     costs?, mitigationRef?, appetiteSnapshot?, supersedesDecisionId? }
  riskDecisions: [],
  // riskObligations: recurring evidence expectations linked to a decision.
  //   { id, riskId, linkedDecisionId, name, expectedEvidenceType,
  //     cadenceDays, graceDays, assigneeId?, lastSatisfiedAt?, active }
  riskObligations: [],

  controlOverrides: {},

  // ── Control ownership ────────────────────────────────────────────────────
  // ISO 27001 A.5.1 requires explicit delegation of control responsibility.
  // Shape: { [scfControlId]: { ownerId, assignedAt, assignedBy, history: [] } }
  // history entries: { fromId, toId, at, by }
  controlOwnership: {},

  // ── Ownership gate settings ───────────────────────────────────────────────
  // gateEnabled: when true, evaluateControl adds Gate 6 — "Control has an
  //   assigned owner" — to the compliance evaluation. Defaults to false so
  //   existing tenants can assign owners before the gate fires.
  ownershipSettings: {
    gateEnabled: false,
  },

  // ── Control effectiveness ─────────────────────────────────────────────────
  // ISO 27001:2022 clause 9.1 — evaluation of information security performance.
  // Separate from the binary pass/fail gates: a control can pass all gates yet
  // still be rated "Developing" because the evidence is thin.
  // Shape: { [scfControlId]: { score: 1|2|3|4|5, justification: string,
  //                             ratedAt: ISO, ratedBy: userId } }
  // Scores: 1=Initial, 2=Developing, 3=Defined, 4=Managed, 5=Optimised
  controlEffectiveness: {},

  gapAnalyses: [],
  threatAnalyses: [],
  documentTypes: [],

  // ── Document number registry ────────────────────────────
  // Persistent highwater mark per type abbreviation (e.g. { POL: 5, PRO: 12 }).
  // Each issued number bumps its abbreviation's value. Numbers are NEVER
  // reissued — even after a document is soft-deleted or hard-purged by the
  // retention engine — so the registry survives independently of which
  // documents currently exist. See src/utils/documentNumbering.js.
  documentNumberSequences: {},

  // ── Soft-deleted documents ──────────────────────────────
  // Documents removed via the Documents UI are moved here (not hard-deleted)
  // and stay restorable from the super-admin portal until the retention
  // engine purges them after activityLogSettings.retentionDays. Each entry
  // carries the original document plus deletedAt (ISO) and deletedBy (userId).
  // Tenant users never read this array — it is invisible to every UI read path.
  // See DOC-B-003 in minimum-requirements.json.
  deletedDocuments: [],

  // ── Statement of Applicability (SoA) ────────────────────
  // Per-framework SoA data: soaData[frameworkId] = { controls: { [controlId]: { applicable, justification, implementationStatus } } }
  soaData: {},
  // Versioned SoA snapshots: soaVersions[frameworkId] = [ { id, version, label, savedAt, savedBy, controls,
  //   approvalStatus: 'pending'|'approved'|'rejected',
  //   approvalRecord: { approvedBy, approvedAt, comment, requestKey? } | null — written
  //     only by the soa-approve server action (msr 4.1b, SOA-B-001/002),
  //   rejectionRecord: { rejectedBy, rejectedAt, comment } | null } ]
  // Versions without approvalStatus were saved before this field existed and are treated as 'approved'.
  soaVersions: {},
  // SOA approval authority settings.
  // Shape: { frameworkApprovers: { [frameworkId]: userId } }
  // The designated approver must formally approve each saved SOA version before it is
  // considered an official audit record (ISO 27001:2022 clause 6.1.3(d)).
  soaSettings: {
    frameworkApprovers: {},
  },

  // ── Document approval settings (DOC-B-007) ───────────────
  // allowOwnerWriterOverride: when true (default), a document owner who is
  // also its writer may self-override the I-17 segregation-of-duties block
  // and approve their own document, provided a justification comment is
  // supplied. The override is always flagged (approvalRecord.sodOverride)
  // and enforced server-side regardless of this setting's client display.
  documentApprovalSettings: {
    allowOwnerWriterOverride: true,
  },

  // ── Management Systems ───────────────────────────────────
  // Configurable list of management systems for document numbering.
  // Each entry: { id, name, abbreviation }
  managementSystems: [],

  // ── Asset Management ────────────────────────────────────────
  // Centralised asset registry (ISO 27002 A.5.9 / SCF-AST-001 / CIS-1.1)
  // Each asset: { id, name, type, subType, status, category, description,
  //   manufacturer, model, serialNumber, assetTag, hostname, ipAddress, macAddress, osName, osVersion,
  //   ownerId (employee), assigneeId (employee), vendorId, departmentTag,
  //   location, locationDetail,
  //   purchaseDate, purchaseValue, purchaseCurrency, warrantyExpiry, leaseExpiry, depreciationMethod, usefulLifeMonths,
  //   dataClassification, containsPersonalData, personalDataCategories, dpiaNotes,
  //   backupEnabled, backupFrequency, backupTarget, encryptionAtRest, encryptionInTransit,
  //   populationSource, externalId, lastSyncAt,
  //   nextAuditDate, lastAuditDate, lastAuditBy,
  //   controls (linked TCF control IDs),
  //   aiSystemRole: null | string — ISO 42001 A.4.2 / EU AI Act lifecycle role
  //     (shown only when fw-iso42001 or fw-eu-ai-act is enabled)
  //   maintenanceLog: [{ id, date, type, description, performedBy, cost, nextDue }],
  //   history: [{ id, action, timestamp, userId, details }],
  //   tags: [],
  //   notes, createdAt, createdBy, updatedAt, updatedBy }
  assets: [],

  // ── Asset Settings ──────────────────────────────────────────
  assetSettings: {
    // Custom asset types beyond the defaults
    customTypes: [],
    // Data classification labels (can be customised per tenant)
    dataClassificationLabels: ['Public', 'Internal', 'Confidential', 'Restricted'],
  },

  // ── Vendor Risk Management ────────────────────────────────
  vendors: [],

  // ── Vendor Assessment Configuration ───────────────────────
  // Tenant-configurable vendor question library. Seeded from the historical
  // hardcoded assessment (DEFAULT_VENDOR_SECTIONS) so behaviour is identical
  // until an admin edits it via Vendors → Assessment Setup. Shape:
  //   { version, tiers: [{ level, label, requiresAssessment }],
  //     baseSections: [{ id, label, icon,
  //                      questions: [{ id, text, appliesToTier }] }],
  //     categoryOverrides: { [category]: { extraSections: [...] } } }
  // Effective questions per vendor are resolved by src/utils/vendorQuestions.js
  // (base + category override, filtered by the vendor's tier level).
  vendorAssessmentConfig: buildDefaultVendorAssessmentConfig(),

  // ── Document Assignments ────────────────────────────────
  // Admin-created assignments that declare which documents employees or groups
  // must read. Target is resolved at read time (not snapshotted). Satisfaction
  // rule: employee.documentAcknowledgements must contain an entry matching
  // (documentId, doc.version) where doc.version is the current latest version.
  // Shape: { id, documentId, assignedAt, assignedBy,
  //   target: { type: 'employee'|'department'|'team'|'role', value: string },
  //   deadline: ISO|null, note: string, active: boolean }
  documentAssignments: [],

  // ── Evidence & Attestation ──────────────────────────────
  // Tenant-configurable requirement definitions. Each entry declares what
  // evidence is required, who must provide it, when, and how it links to
  // frameworks/controls. isSensitive=true triggers the view-verify-delete
  // workflow: the file blob is deleted after verification and only metadata
  // (type, uploader, verifier, dates) is retained.
  // Shape: { id, name, description,
  //   evidenceType: string,   // 'criminal-record'|'certification'|'training-attestation'|'id-check'|'acknowledgement'|custom
  //   isSensitive: boolean,
  //   allowedFileTypes: string[],   // e.g. ['pdf','jpg'] — empty = any
  //   target: { type: 'all'|'role'|'department'|'team'|'employment-type'|'specific', value: string|null },
  //   trigger: 'on-hire'|'on-role-change'|'manual'|'recurring',
  //   frequency: 'one-time'|'annual'|'biennial'|'quarterly'|'custom'|null,
  //   frequencyDays: number|null,   // used when frequency='custom'
  //   gracePeriodDays: number,      // days after trigger before overdue (default 30)
  //   linkedFramework: string|null, linkedControlRef: string|null, linkedObligationId: string|null,
  //   reviewerRoles: string[],      // tenant user roles that may review sensitive evidence
  //   active: boolean,
  //   createdAt, createdBy, updatedAt }
  evidenceRequirements: [],

  // Per-employee evidence instances — one record per (employee, requirement) trigger event.
  // Sensitive docs: blobName + fileName are set to null after verification (post-deletion).
  // Compliance window: validFrom = verifiedAt, validUntil = verifiedAt + frequency period.
  // For recurring requirements, nextDueAt is set at verification time.
  // Shape: { id, requirementId, employeeId,
  //   employeeName, requirementName, evidenceType, isSensitive,   // denormalized snapshots
  //   status: 'pending'|'submitted'|'under-review'|'verified'|'rejected'|'expired',
  //   triggeredAt, triggeredBy,
  //   triggerReason: 'on-hire'|'role-change'|'annual-renewal'|'manual',
  //   dueAt,
  //   submittedAt, submittedBy, blobName, fileName, fileSize,
  //   reviewedAt, reviewedBy,
  //   verifiedAt, verifiedBy, verifierNote,
  //   documentDeletedAt, documentDeletedBy,
  //   validFrom, validUntil, nextDueAt,
  //   rejectedAt, rejectedBy, rejectionReason,
  //   notes, createdAt, updatedAt }
  evidenceRecords: [],
  // Control-owner portal answers waiting for review. Staging only — nothing that
  // evaluates compliance reads this; the reviewer's accept is what mints the
  // artefact or attestation the engine then sees (COP-B-002).
  portalSubmissions: [],

  // ── Employee Management ─────────────────────────────────
  // Sample employees (employeeGuideData.js) are opt-in only, added via the
  // EmployeeLifecycleWizard onboarding guide — never seeded here.
  employees: [],

  // ── Candidate / Pre-employment Screening (SJML) ─────────
  // Candidates are separate from employees until hired.
  // Access is gated by hrRecruitment permission on the user record.
  candidates: [],

  // ── Employee Evaluation Criteria (admin-defined) ────────
  // Sample criteria (employeeGuideData.js) are opt-in only, added via the
  // EmployeeLifecycleWizard onboarding guide — never seeded here.
  employeeEvaluationCriteria: [],

  // ── Employee Compliance Feedback Settings ────────────────
  employeeFeedbackSettings: {
    // Categories for quick compliance feedback (tenant-configurable)
    categories: ['General', 'Security Awareness', 'Policy Compliance', 'Professional Conduct'],
    // Whether to hide who gave feedback in the employee portal
    anonymousInPortal: false,
  },

  // ── Employee Settings ───────────────────────────────────
  employeeSettings: {
    // Email domains owned by the tenant. When two domains are listed, addresses
    // that share a local part across any of them resolve to the same identity
    // for asset / employee / sync matching. See src/utils/emailIdentity.js.
    tenantDomains: [],
    // Which fields employees can self-edit via the portal
    portalEditableFields: ['phone', 'emergencyContact'],
    // Default JML checklist templates
    jmlTemplates: {
      joiner: [
        { key: 'contract-signed', label: 'Employment contract signed' },
        { key: 'nda-signed', label: 'NDA / Confidentiality agreement signed' },
        { key: 'screening-complete', label: 'Pre-employment screening complete' },
        { key: 'it-accounts', label: 'IT accounts provisioned' },
        { key: 'assets-issued', label: 'Assets issued (laptop, badge, phone, etc.)' },
        { key: 'security-training', label: 'Security awareness training assigned' },
        { key: 'policies-acknowledged', label: 'Key policies acknowledged' },
        { key: 'manager-intro', label: 'Manager introduction and onboarding plan' },
        { key: 'acceptable-use', label: 'Acceptable use policy signed' },
      ],
      mover: [
        { key: 'access-review', label: 'Access rights reviewed and updated' },
        { key: 'asset-reassignment', label: 'Assets reassigned if needed' },
        { key: 'new-manager', label: 'New manager assigned' },
        { key: 'role-update', label: 'Role and responsibilities updated' },
        { key: 'training-review', label: 'Training requirements reviewed' },
        { key: 'data-handover', label: 'Data and knowledge handover complete' },
      ],
      leaver: [
        { key: 'exit-interview', label: 'Exit interview conducted' },
        { key: 'asset-return', label: 'All assets returned and verified' },
        { key: 'access-revoked', label: 'All system access revoked' },
        { key: 'email-forwarding', label: 'Email forwarding / delegation set up' },
        { key: 'knowledge-transfer', label: 'Knowledge transfer complete' },
        {
          key: 'legal-obligations',
          label: 'Post-employment legal obligations communicated (NDA, non-compete)',
        },
        { key: 'final-payroll', label: 'Final payroll and benefits confirmed' },
        { key: 'data-backup', label: 'Personal data removed / archived per policy' },
      ],
    },
  },

  // ── Role Definitions (tenant-wide) ──────────────────────
  // Each role has a name, description, and list of responsibility statements.
  // Assigned to employees in Phase 2; acknowledged via portal in Phase 4.
  roleDefinitions: [],

  // ── Security Awareness Training ─────────────────────────
  // Admin-created or imported quiz-based training modules.
  // Completion records live on state.employees[].trainingCompletions[] (added in Phase 2).
  // Shape: { id, title, description, estimatedMinutes, version, passThreshold,
  //   slides: [{ id, title, content, imageUrl }],
  //   quiz: [{ id, question, options: [{ id, text }], correctOptionId }],
  //   assignments: [{ id, target: { type: 'all'|'department'|'specific', value: string|null },
  //                   assignedAt, assignedBy, deadline: ISO|null }],
  //   createdAt, createdBy, updatedAt, updatedBy, archivedAt: ISO|null }
  trainingModules: [],

  // ── Training Programs ────────────────────────────────────
  // Ordered collections of modules assigned to employees with program-level criteria.
  // Shape: { id, title, description,
  //   modules: [{ moduleId, order }],
  //   criteria: { completionMode: 'all' },
  //   assignments: [{ id, target: { type, value }, assignedAt, assignedBy, deadline }],
  //   createdAt, createdBy, updatedAt, updatedBy, archivedAt: ISO|null }
  trainingPrograms: [],

  // ── User Access Review (UAR) ─────────────────────────────
  // Shape: { id, title, description, scope: { type: 'all'|'department'|'employment-type', value },
  //   reviewers: [{ userId, userName, email }],
  //   deadline: ISO|null, status: 'draft'|'active'|'closed'|'cancelled',
  //   createdAt, createdBy, activatedAt: ISO|null,
  //   closedAt: ISO|null, closedBy: string|null,
  //   decisions: [], evidenceId: null }
  uarCampaigns: [],

  // ── Activity log settings ────────────────────────────────
  // retentionDays: how long to keep log rows in Azure SQL (30–365, default 90)
  activityLogSettings: {
    retentionDays: 90,
  },

  // ── Business Impact Analysis ─────────────────────────────
  // BIA assessments — one per asset/system. Each tracks impact scores,
  // recovery objectives (RTO, RPO, MTPD), dependencies, and stakeholder assignments.
  biaAssessments: [],

  // ── Criticality Scale ────────────────────────────────────
  // The ONE shared criticality definition for the whole system. Every module
  // that rates "how critical is this thing" (assets, BIA, customers, vendors,
  // supplier involvements, and anything added later) reads its level names,
  // explanations, and colours from here — no module hardcodes its own labels.
  // Admin-configurable: 3–6 levels, values are always contiguous 1..N (1 = least
  // critical, N = most critical). Each record stores its OWN value on this scale;
  // the scale is the shared vocabulary, not a shared value. Source of truth for
  // the level vocabulary — see minimum-requirements/criticality.json (CRIT-B-001).
  criticalityScale: {
    levels: [
      {
        value: 1,
        name: 'Very Low',
        description: 'Negligible impact if compromised, lost, or disrupted.',
        color: '#64748b',
      },
      { value: 2, name: 'Low', description: 'Minor, easily-absorbed impact.', color: '#facc15' },
      {
        value: 3,
        name: 'Medium',
        description: 'Noticeable impact requiring active management.',
        color: '#fb923c',
      },
      {
        value: 4,
        name: 'High',
        description: 'Serious impact on operations, finances, or obligations.',
        color: '#f43f5e',
      },
      {
        value: 5,
        name: 'Critical',
        description: 'Severe or existential impact; highest priority.',
        color: '#dc2626',
      },
    ],
    updatedAt: null,
    updatedBy: null,
  },

  // ── Subcontractor Roles ──────────────────────────────────
  // Tenant-editable list of role options offered in the Subcontractors →
  // Add Involvement wizard ("Assign roles" step). Admin-configurable in
  // Settings → Supplier Roles. Falls back to DEFAULT_SUBCONTRACTOR_ROLES
  // (src/utils/subcontractorRoles.js) when empty, so existing tenants keep
  // the same options they had before this became configurable.
  subcontractorRoles: [
    { value: 'project-manager', label: 'Project Manager', description: '' },
    { value: 'programme-manager', label: 'Programme Manager', description: '' },
    { value: 'it-architect', label: 'IT Architect', description: '' },
    { value: 'security-consultant', label: 'Security Consultant', description: '' },
    { value: 'developer', label: 'Developer', description: '' },
    { value: 'tester', label: 'Tester', description: '' },
    { value: 'infrastructure-specialist', label: 'Infrastructure Specialist', description: '' },
    { value: 'operations-specialist', label: 'Operations Specialist', description: '' },
    { value: 'data-specialist', label: 'Data Specialist', description: '' },
    { value: 'external-sme', label: 'External SME', description: '' },
  ],

  // ── BIA Settings ─────────────────────────────────────────
  // Tenant-configurable BIA impact categories and time intervals
  biaSettings: {
    impactCategories: [
      { id: 'financial', label: 'Financial', icon: '◈' },
      { id: 'operational', label: 'Operational', icon: '◉' },
      { id: 'reputational', label: 'Reputational', icon: '◎' },
      { id: 'legal', label: 'Legal / Regulatory', icon: '◫' },
      { id: 'safety', label: 'Health & Safety', icon: '◇' },
    ],
    timeIntervals: [
      { id: '1h', label: '1 hour' },
      { id: '4h', label: '4 hours' },
      { id: '8h', label: '8 hours' },
      { id: '24h', label: '24 hours' },
      { id: '48h', label: '48 hours' },
      { id: '72h', label: '72 hours' },
      { id: '1w', label: '1 week' },
      { id: '2w', label: '2 weeks' },
      { id: '1m', label: '1 month' },
    ],
    // Labels and colors here are the source of truth for the wizard's
    // severity dots AND must match the criticality bucketing in
    // src/components/bia/BIAWizard.jsx → computeCriticality():
    //   max score 5 → Critical, 4 → High, 3 → Medium, 1–2 → Low, 0 → None.
    // Values 1 and 2 both bucket to "Low" — they share the same label/color
    // here so what the user picks on the timeline is what they see as the
    // overall criticality on the asset badge, workbench column, and BIA list.
    impactLevels: [
      { value: 0, label: 'None', color: '#64748b' },
      { value: 1, label: 'Low', color: '#facc15' },
      { value: 2, label: 'Low', color: '#facc15' },
      { value: 3, label: 'Medium', color: '#fb923c' },
      { value: 4, label: 'High', color: '#f43f5e' },
      { value: 5, label: 'Critical', color: '#dc2626' },
    ],
  },

  // ── ROPA (Records of Processing Activities) ─────────────
  // GDPR Art. 30 — structured register of processing activities.
  // Each entry: { id, name, description, purposes, lawfulBasis, liaReference,
  //   dataSubjectCategories, dataCategories, specialCategory, specialCategoryCondition,
  //   recipients, thirdCountryTransfers: [{ country, mechanism, tiaReference }],
  //   retentionPeriod, securityMeasures (TCF control IDs), dataSource,
  //   vendorIds, documentIds,
  //   status: 'active'|'draft'|'retired',
  //   createdAt, createdBy, updatedAt, updatedBy, lastReviewed, nextReview,
  //   changeHistory: [{ id, timestamp, userId, userName, changes }] }
  ropaEntries: [],

  // GDPR Art. 35 Data Protection Impact Assessments.
  // Each DPIA may carry linkedRiskId (optional, additive) — set when the residual risk is promoted
  // to the risk register via the "Create risk" button in DPIAWorkflow. Undefined on pre-conversion DPIAs.
  dpias: [],

  // ── Integrations ────────────────────────────────────────
  // Per-tenant integration configuration. Each key is an integration ID.
  // Shape: { [integrationId]: { enabled, config, syncMode, pollInterval, connectedAt, connectedBy, lastSync, lastSyncStatus } }
  // Credentials are NOT stored here — they live server-side in tenant_integrations table (encrypted).
  integrations: {},

  // ── GitHub PR audit ─────────────────────────────────────
  // Derived by the server-side github-pr-audit integration handler for the
  // ecurity (ISMS-owner) tenant only. Read-only from the frontend. Auto-verifies
  // obl-sdlc-peer-review and obl-sdlc-regression-test via state.githubAudit.checks.
  // null until the first sync. Schema: docs/state-schema.md — githubAudit.
  githubAudit: null,

  // ── Email settings ─────────────────────────────────────
  // welcomeEmailEnabled: opt-in auto-send welcome email on user creation (default false)
  emailSettings: {
    welcomeEmailEnabled: false,
  },

  // ── Compliance digest settings ───────────────────────────
  // Controls the weekly/monthly compliance posture summary email.
  // Schema: docs/state-schema.md — complianceDigestSettings.
  // Server-side timer: compliance-digest-scheduler (Mondays 07:00 UTC).
  complianceDigestSettings: {
    enabled: false,
    cadence: 'weekly', // 'weekly' | 'monthly'
    recipients: [], // [{ email: string, name: string }]
    lastSentAt: null, // ISO string — set by the scheduler after each send
    lastScore: null, // number|null — legacy: no longer written or read; the digest delta comes from the daily control-status pulse (GH #7166)
  },

  // ── Email breach monitoring settings ────────────────────
  // Controls the weekly email breach scan (Wed 03:00 UTC).
  // Schema: docs/state-schema.md — emailBreachSettings.
  // Server-side timer: email-breach-scanner.
  emailBreachSettings: {
    enabled: true,
    scanScope: ['employees', 'users', 'vendors'], // which entity types to include
    notifyAdmins: true,
    lastScanAt: null, // ISO string — written by the scanner
    lastScanSummary: null, // { totalEmails, newFindings, partial, partialReason }
  },

  // ── Billing settings ────────────────────────────────────
  // Customer-side invoice details, set by tenant admin in Settings > Billing.
  // Issuer-side details (CVR, IBAN, BIC, VAT rate, etc.) live in the
  // billing_config DB row and are super-admin-only.
  // billingCadence: 'monthly' | 'yearly'
  // currency: '' | 'EUR' | 'DKK' — '' = use issuer default
  // paymentTermsDaysOverride: null | number — null = use issuer default
  // paymentTermsMarkupPercent: number — surcharge for longer payment terms
  billing: {
    companyName: '',
    vatId: '',
    addressLine1: '',
    addressLine2: '',
    postalCode: '',
    city: '',
    country: '',
    billingEmail: '',
    billingContactName: '',
    billingCadence: 'yearly',
    currency: '',
    paymentTermsDaysOverride: null,
    paymentTermsMarkupPercent: 0,
    poNumber: '',
    notes: '',
  },

  // ── AI Learning settings ────────────────────────────────
  // When enabled, documents saved in the editor with control mappings
  // automatically generate training pairs for the local AI.
  // Controlled by the tenant admin in Settings > AI Learning.
  aiLearningEnabled: false,

  // ── AI Training Pairs ───────────────────────────────────
  // Auto-collected from document saves when aiLearningEnabled is true.
  // Shape: [{ id, docId, docTitle, text, controls, createdAt }]
  aiTrainingPairs: [],

  // ── Control Topics ──────────────────────────────────────
  // Cross-framework groupings — admin-managed, usable as filters everywhere
  controlTopics: BASELINE_CONTROL_GROUPS.map((g) => ({
    id: g.id,
    name: g.name,
    description: g.description,
    icon: g.icon,
    color: g.color,
    order: g.order,
    controlIds: g.controlIds,
  })),

  // ── Incident Management ──────────────────────────────────
  // Incidents: { id, title, description, type, severity, status,
  //   discoveredAt, reportedAt, reportedBy, assigneeId,
  //   personalDataInvolved, dpaNotifiedAt, dataSubjectNotifications,
  //   actions: [{ id, title, ownerId, dueDate, status, closedAt }],
  //   rootCause, contributingFactors, lessonsLearned, preventiveActions,
  //   controlIds, evidence: [{ id, label, url, addedAt, addedBy }],
  //   createdAt, createdBy, updatedAt,
  //   — EU regulatory fields (additive) —
  //   regulatoryProfile: {
  //     isRegulatoryRelevant, classifiedAt, activeFrameworks[],
  //     globalAlertActive, consistencyCheckPassed, consistencyCheckedAt,
  //     consistencyIssues[] },
  //   incidentContext: {
  //     breachType, affectedSystems[], affectedDataCategories[],
  //     estimatedDataSubjectsAffected, estimatedRecordsAffected,
  //     isMalicious, crossBorderImpact, affectedProductIds[],
  //     affectedAiSystemIds[], detectionTimestamp (legacy, never set and not
  //     read — the regulatory clock starts at discoveredAt, GH #7123),
  //     occurrenceTimestamp },
  //   regulatoryStakeholders: {
  //     notificationOwner, dpo, ciso, legalOwner, additionalWatchers[] },
  //   frameworkObligations: {
  //     gdpr|nis2|cra|aiact: {
  //       enabled, thresholdMet, state, thresholdRationale,
  //       deadlines{ <name>: ISO }, clock{ field: 'discoveredAt', startedAt,
  //       finalReportFrom? }, deadlinesMet[] (server-computed from
  //       discoveredAt and the recorded submission),
  //       overdue{}, openItems[], reportDraft, reportVersions[],
  //       submissionRecord { referenceNumber, submittedAt, channel,
  //         stages[] (notification stage(s) it evidences), corrects? (the
  //         history position it replaces), submittedBy, recordedAt,
  //         recordedBy, history[] },
  //       authorityAckReceived,
  //       — msr 4.6, server-owned: thresholdHistory[], deadlineRulesVersion,
  //       supersededOverdue{}, deadlinesRecordedLate[], notifiableAtDeadline,
  //       overdueCatchUp{} (flags the one-off catch-up raised without email);
  //       CRA only, typed: craReportType, correctiveMeasureAvailableAt } } }
  incidents: [],

  // IR Exercises: { id, title, type, scheduledDate, status,
  //   description, participants, outcome, lessonsLearned,
  //   createdAt, createdBy }
  //   Findings raised from an exercise carry `incidentExerciseId` (GH #7881);
  //   the exercise's view of them is derived, never stored here.
  incidentExercises: [],

  // EU regulatory incident-reporting tenant settings (additive)
  // nis2EntityScope: 'essential'|'important'|null
  incidentReportingSettings: {
    nis2EntityScope: null,
    nis2SectorId: null,
    gdprDpoAssigned: false,
    defaultStakeholders: {
      notificationOwner: null,
      dpo: null,
      ciso: null,
      legalOwner: null,
    },
  },

  // Cross-incident regulatory alert index — one entry per incident that has
  // globalAlertActive === true. Maintained by the classifier and the deadline
  // watch timer. Entry shape:
  //   { incidentId, incidentTitle, activeFrameworks[], mostUrgentDeadline,
  //     stakeholderIds[], hasOverdue }
  activeRegulatoryAlerts: [],

  // ── Management Reviews (ISO 27001 Clause 9.3) ────────────────
  // Top management reviews of the ISMS at planned intervals.
  // Shape: { id, title, scheduledDate, conductedDate, chair, attendees,
  //   status: 'scheduled'|'in-progress'|'complete',
  //   inputs: { previousActionsStatus, externalIssuesChanges, ... },
  //   outputs: { continualImprovementOpportunities, ismsChangesNeeded, resourceNeeds },
  //   actions: [{ id, description, owner, dueDate, status, closedAt, closedNote }],
  //   snapshotData: { capturedAt, totalRisks, highRisks, ... },
  //   createdAt, createdBy }
  managementReviews: [],

  // ── Management Review Settings ────────────────────────────────
  // nextReviewDate: ISO date string for when the next review should occur (shown on dashboard)
  // reviewFrequency: 'annual' | 'biannual' | 'quarterly'
  managementReviewSettings: {
    nextReviewDate: null,
    reviewFrequency: 'annual',
  },

  // ── BC/DR Module ─────────────────────────────────────────
  // Business Continuity & Disaster Recovery (ISO A.5.29/5.30/8.13, CIS 11.5/18.x)

  // BCP/DRP plans — each plan covers a set of processes/systems
  // Shape: { id, title, version, type: 'bcp'|'drp'|'ict'|'other',
  //   ownerId, scope, triggerConditions, status: 'draft'|'in-review'|'approved'|'retired',
  //   approverId, approvedAt, reviewDate, documentId,
  //   initiativeId,   // soft ref -> governanceInitiatives[].id when this plan was
  //                   // created/linked from a Governance Initiative's DR Plan deliverable
  //   coveredProcesses, crisisContacts: [{ name, role, phone, email }],
  //   linkedBiaIds: [], controls: [],
  //   notes, createdAt, createdBy, updatedAt, updatedBy }
  bcdrPlans: [],

  // ICT recovery runbooks — step-by-step recovery procedures per system
  // Shape: { id, title, assetId, assetName, recoveryOrder,
  //   rtoHours, rpoHours, ownerId,
  //   status: 'draft'|'active'|'deprecated',
  //   dependencies: [{ assetId, assetName, note }],
  //   steps: [{ id, order, title, description, assigneeId, dependsOn }],
  //   reviewDate,     // next review due, one of several sources — see
  //                   // src/utils/runbookReview.js (earliest wins)
  //   reviews: [{ id, at, by, byName, outcome, note, coversUpdatedAt,
  //               withdrawnAt, withdrawnBy }],  // append-only review log
  //   documentId,     // soft ref -> documents[].id when generated by a Governance
  //                   // Initiative's Recovery Procedures deliverable
  //   initiativeId,   // soft ref -> governanceInitiatives[].id, same origin as documentId
  //   notes, createdAt, createdBy, updatedAt, updatedBy }
  bcdrRunbooks: [],

  // Live executions of a runbook during an actual or simulated incident.
  // Shape: { id, runbookId, runbookTitle,
  //   startedAt, completedAt (null until done),
  //   startedBy (userId), startedByName,
  //   stepStatuses: { [stepId]: 'pending'|'in-progress'|'done'|'blocked' },
  //   stepUpdatedAt: { [stepId]: ISO },
  //   notes: { [stepId]: string } }
  bcdrRunbookRuns: [],

  // Backup management register (ISO A.8.13)
  // Shape: { id, assetId, assetName,
  //   backupType: 'full'|'incremental'|'differential'|'snapshot',
  //   frequency: 'realtime'|'hourly'|'daily'|'weekly'|'monthly'|'custom',
  //   frequencyDetail, retentionPeriod, destination,
  //   encryptionEnabled: bool, offsite: bool, ownerId,
  //   status: 'active'|'paused'|'failing',
  //   nextTestDue,
  //   testLog: [{ id, testedAt, testedBy, result: 'success'|'partial'|'fail', recoveryTimeMins, notes }],
  //     ^ FROZEN LEGACY (BCDR-B-004): read-only, never added to; restore tests
  //       are recorded in restoreTests[] with backupId.
  //   notes, createdAt, createdBy, updatedAt }
  bcdrBackups: [],

  // DR test register — exercises (tabletop, simulation, full failover)
  // Shape: { id, title, type: 'tabletop'|'walkthrough'|'simulation'|'failover',
  //   status: 'planned'|'in-progress'|'completed'|'cancelled',
  //   scheduledDate, conductedDate, facilitatorId,
  //   scope, scenario,
  //   participantIds: [],
  //   outcome: 'pass'|'partial'|'fail'|null,
  //   rtoAchievedHours, rpoAchievedHours,
  //   lessonsLearned,
  //   // FROZEN LEGACY (BCDR-B-002). A weakness an exercise finds is now a
  //   // record in the one findings register (source 'dr-test' + drTestId).
  //   // This array is read, shown and closable but can no longer grow; a row
  //   // someone raises into the register carries findingId, and a findingId
  //   // that resolves to nothing counts as OPEN, never as resolved:
  //   followUpActions: [{ id, description, ownerId, dueDate,
  //                       status: 'open'|'closed', findingId? }],
  //   linkedPlanIds: [], linkedBiaIds: [],
  //   linkedRunbookIds: [],   // the runbooks this exercise exercised. A test
  //                           // that did NOT pass brings their review forward;
  //                           // a passed one never renews it (runbookReview.js)
  //   alertValidationNotes,   // CIS 11.5
  //   notes, createdAt, createdBy, updatedAt }
  bcdrDrTests: [],

  // Pen test programme (CIS 18.1/18.2/18.4/18.5)
  // Shape: { id, title,
  //   type: 'external'|'internal'|'web-app'|'physical'|'social-engineering',
  //   status: 'planned'|'in-progress'|'completed'|'remediation',
  //   provider, providerType: 'internal'|'external',
  //   scope, startDate, endDate,
  //   findingSummary: { critical, high, medium, low, informational, topCvss },
  //   findings: [{ id, title, severity: 'critical'|'high'|'medium'|'low'|'informational',
  //     cvss, description, recommendation,
  //     status: 'open'|'in-progress'|'closed'|'accepted',
  //     linkedTaskId, linkedRiskId, verifiedAt }],
  //   frequency: 'annual'|'biannual'|'quarterly'|'continuous',
  //   nextTestDue,
  //   linkedControlIds: [],
  //   notes, createdAt, createdBy, updatedAt }
  bcdrPenTests: [],

  // ── Evidence Request Register (ISO 27001 Clause 9.2) ───────
  // Named evidence items to be gathered for an audit engagement. The record is
  // the DOMAIN object — it owns the evidence; the work of producing that
  // evidence is a Tier 2 domain-backed action in actions[], the same shape
  // risk.actions[] uses (ADR-0003, CLAUDE.md § One Task System).
  // Shape: { id, name, description, category (controlTopicId), controlIds[],
  //          notApplicable (bool), naReason,
  //          attachedDocIds[], attachedArtefactIds[], linkedAuditId,
  //          isSystemItem (bool), catalogueId, derivationKey,
  //          actions[] (DomainAction: { id, title, description, status
  //            (pending|in-progress|completed), assigneeId, dueDate, createdAt,
  //            completedAt, completedBy, evidenceRefs[] }),
  //          legacy: { status, ownerId, migratedAt },  ← frozen pre-ADR-0003
  //            archive, never read or written by the UI
  //          createdAt, updatedAt }
  evidenceRequests: [],

  // ── Findings Register (ISO 27001 Clause 9.2 / 10.1) ────────
  // The ONE nonconformity and corrective-action register — raised from audits,
  // controls, supplier requirement reviews, or manually. A domain that needs to
  // escalate a gap extends THIS record (an optional FK + a `source` value); it
  // never gets a register of its own (FIND-B-003).
  //
  // Stored per-record in dbo.tenant_finding_entities (SKIE-03a) and overlaid
  // here by state-read; state-write strips this key once the tenant has a store
  // row. Written only through the findings-write Azure Function — `id` and
  // `ref` are both minted server-side (ID-GEN-001/002) and `ref` is immutable
  // after create.
  //
  // Shape: { id, ref ('F-YYYY-NNN'), title, description,
  //   source: 'internal-audit'|'external-audit'|'management-review'
  //          |'supplier-requirement-review'|'dr-test'|'ir-exercise'|'incident'
  //          |'control-failure'|'other',
  //   sourceRef, sourceQuestionId,
  //   severity: 'major'|'minor'|'observation',
  //   capaType: 'corrective'|'preventive',
  //   detectedDate, detectedBy, owner,
  //   status: 'open'|'in-remediation'|'pending-verification'|'closed'|'accepted-risk',
  //   immediateAction, rootCause, rootCauseMethod, rootCauseCategory,
  //   // Optional structure behind the method (GH #6421) — five linked answers
  //   // for 5-Why, causes per category for fishbone. `rootCause` stays the one
  //   // canonical narrative and is derived from these when a method is set:
  //   rootCauseAnalysis?: { whys[5], fishbone{}, priorNarrative },
  //   correctiveAction, correctiveActionDueDate,
  //   // Effectiveness verification is a LOG of dated checks, not one snapshot
  //   // (components/findings/verificationLog.js). The four flat fields below
  //   // are the projection of its LAST entry, kept because every existing
  //   // reader reads them; findings predating the log carry only those four
  //   // and get a synthesised first entry on read:
  //   verificationChecks[]: { id, checkedOn, checkedBy, outcome, note,
  //                           recordedAt, recordedBy },
  //   verificationDate, verifiedBy, verificationNote, verificationOutcome,
  //   linkedTaskIds[], linkedControlIds[], closedDate, closedBy,
  //   // Supplier linkage (SKIE-03a) — all optional, all SOFT references:
  //   involvementId, deliveryAgreementId, requirementExternalId, evidenceIds[],
  //   // Internal-audit linkage — optional, SOFT reference (AFR A-01):
  //   internalAuditId,  // -> internalAudits[].id, set when raised from an audit
  //   // Continuity-exercise linkage — optional, SOFT reference (BCDR-B-002):
  //   drTestId,         // -> bcdrDrTests[].id, set when raised from an exercise
  //   // IR-exercise linkage — optional, SOFT reference (GH #7881):
  //   incidentExerciseId, // -> incidentExercises[].id, raised from its lessons
  //   // Control-test linkage (msr 3.2) — SERVER-OWNED, set only by
  //   // control-tests-write when a partial/failed test raises the finding
  //   // (source 'control-failure', sourceRef 'control-test:<id>'):
  //   controlTestId,    // -> tenant_msr_entities 'control-test' id
  //   createdAt, createdBy, updatedAt, statusHistory[] }
  findings: [],

  // ── Finding proposals (SKIE-03, VENZO process step 8) ──────
  // READ-ONLY OVERLAY — never persisted in this blob. state-read populates it
  // from dbo.tenant_finding_entities (entity_type 'findingProposal'), filtered
  // to status 'proposed'; state-write strips it unconditionally.
  //
  // Requirement gaps escalated from verdicts a human recorded on a
  // subcontractor involvement's SKI 02.15 matrix — `not-fulfilled` or
  // `not-documented` only. A proposal is NOT a finding: it counts toward
  // nothing until a named human raises it through finding-proposals-write,
  // which creates the nonconformity server-side with the supplier FKs attached
  // (FIND-B-004). Mirrors riskProposals / incidentProposals.
  //
  // Shape: { id ('fp:<involvementId>:<requirementExternalId>'),
  //   status: 'proposed'|'accepted'|'dismissed'|'withdrawn',
  //   involvementId, deliveryAgreementId, requirementExternalId, evidenceIds[],
  //   verdictStatus, suggestedSeverity ('major'|'minor'|'observation'),
  //   suggestedTitle,
  //   sourceSignal: { type:'requirement-verdict', assessment, gaps, comments },
  //   verdictReviewedBy, verdictReviewedAt, detectedAt, createdAt, updatedAt,
  //   // on accept:   acceptedAt, acceptedBy, linkedFindingId, linkedFindingRef
  //   // on dismiss:  dismissedAt, dismissedBy, dismissReason
  //   // on withdraw: withdrawnAt, withdrawReason }
  findingProposals: [],

  // ── Audit Programmes (ISO 27001 Clause 9.2) ─────────────
  // Planned annual audit programmes, each containing scheduled audit activities.
  // Shape: { id, name, year, objective, status: 'planned'|'in-progress'|'complete',
  //   audits: [{ id, name, scope, leadAuditorId, targetDate,
  //     status: 'planned'|'in-progress'|'complete',
  //     linkedAssessmentId,     // soft FK -> gapAnalyses[].id (type 'internal_audit')
  //     internalAuditId,        // soft FK -> internalAudits[].id, set by "Start audit"
  //     // Carried across from the cycle slot when materialised (APLA-05) —
  //     // never re-entered on this screen:
  //     objective, participantIds: [], auditeeId, methods: [], samplingNote,
  //     evidenceDueAt,
  //     cyclePlanId,            // soft FK -> auditCyclePlans[].id
  //     cycleAssignmentId }],   // soft FK -> auditCyclePlans[].assignments[].id
  //                             // absent on an ad-hoc audit that was never in
  //                             // the cycle (a breach follow-up, say)
  //   createdAt, createdBy }
  auditProgrammes: [],

  // ── Internal Audits (ISO 27001 Clause 9.2) ───────────────
  // Executed internal audit records. Control evaluation happens via a linked
  // Progress Assessment (gapAnalyses of type 'internal_audit', back-linked via
  // gapAnalyses[].parentAuditId), not an inline controlAssessments array.
  // Shape: { id, ref ('IA-YYYY-NNN'), name, status: 'draft'|'in-progress'|'complete',
  //   leadAuditorId, auditDate, scope, linkedAssessmentIds: [],
  //   // Who PERFORMED it (FIND-B-009). Seeded from the programme item when the
  //   // audit is started and owned by the audit thereafter: the plan records
  //   // who was expected, this records who went, and a team change on the day
  //   // belongs here. Absent on an audit started before these existed, which
  //   // is why auditReport.js falls back to the programme item.
  //   participantIds: [],  // soft FKs -> users
  //   auditeeId,           // soft FK -> users
  //   methods: [],         // AUDIT_METHODS ids (cycleEffortModel.js)
  //   // Programme linkage — both optional/soft, absent for an audit created
  //   // standalone from the Internal Audits tab rather than from a programme:
  //   programId,          // soft FK -> auditProgrammes[].id
  //   programAuditItemId, // soft FK -> auditProgrammes[].audits[].id
  //   conclusion,         // the audit's own verdict — stays HERE, and is
  //                       // never duplicated into report below, so there is
  //                       // only ever one field that can state it.
  //   // Report (APLA-06) — step 7 of the audit process. Optional in full: an
  //   // audit completed before this existed has none and stays valid, and
  //   // nothing gates audit completion on it.
  //   report: { summary, limitations, positiveObservations: [],
  //     auditedAt: { from, to }, attendees: [],   // soft FKs -> users
  //     distributedTo: [],                        // soft FKs -> users
  //     version, issuedAt, issuedBy,
  //     issueHistory: [{ version, issuedAt, issuedBy }] },
  //   // The report ASSEMBLES the rest at read time (auditReport.js): criteria,
  //   // findings from the one register (FIND-B-003) and evidence are
  //   // referenced, never copied, so a finding corrected later shows the
  //   // correction and no stale second copy can disagree with the register.
  //   createdAt, createdBy, updatedAt }
  internalAudits: [],

  // ── External audits (PEA-01, GH #7123) ────────────────────
  // Audits performed by a certification body or another independent party,
  // recorded once they have taken place. NOT internalAudits[] (FIND-B-010:
  // no clause 9.2 derivation may count them) and not a certificate
  // (FIND-B-006). Findings they raised are in findings[] with source
  // 'external-audit' and a soft externalAuditId.
  // Shape: { id, name, auditBody, auditKind ('certification'|'surveillance'|
  //   'recertification'|'other'), startDate, endDate (YYYY-MM-DD),
  //   auditDays (> 0, whole or half days), createdAt, createdBy, updatedAt,
  //   updatedBy }
  externalAudits: [],

  // ── Restore tests (BCR-02, msr 2.5b, GH #7123) ────────────
  // A restore of an asset's backup, recorded when performed. Written only by
  // restore-tests-write (state-write keeps the stored copy); append-only, a
  // mistake is voided with a reason. The backup modal records here too, with
  // backupId set; bcdrBackups[].testLog is frozen legacy (BCDR-B-004).
  // Shape: { id, assetId, backupId|null, result ('success'|'partial'|'fail'),
  //   rpoAchievedMins|null, rtoAchievedMins|null, tester, evidence,
  //   notes|null, performedAt, recordedBy (server-stamped),
  //   voidedAt?, voidedBy?, voidReason? }
  restoreTests: [],

  // ── Asset reviews (AIM-02, msr 2.5b, GH #7123) ────────────
  // A review of an asset, recorded when done, with an outcome; and the review
  // cycle per asset type (1–365 days, default 365), each change appended.
  // Written only by asset-reviews-write (state-write keeps the stored copy).
  // assetReviews: { id, assetId, assetType, outcome ('confirmed'|'updated'|
  //   'issues-found'), note|null, reviewedAt, reviewedBy (server-stamped),
  //   voidedAt?, voidedBy?, voidReason? }
  // assetReviewCycles: { id, assetType, days, setAt, setBy (server-stamped) }
  assetReviews: [],
  assetReviewCycles: [],
  // msr 4.5a (BCR-03, TPS-05). Server-stamped and append-only; state-write
  // keeps the stored copy of each.
  // biaReviews: { id, biaId, outcome ('confirmed'|'issues-found'), note,
  //   reviewedAt, reviewedBy, voidedAt?, voidedBy?, voidReason? } — bia-reviews-write
  // biaReviewCycles: { id, biaId, days (1–365), setAt, setBy } — bia-reviews-write
  // biaSupplierDeclarations: { id, biaId, noExternalSupplier, note, declaredAt,
  //   declaredBy } — bia-reviews-write
  // vendorExitPlans: { id, vendorId, documentId|null, note, setAt, setBy } —
  //   vendors-write set-exit-plan
  biaReviews: [],
  biaReviewCycles: [],
  biaSupplierDeclarations: [],
  vendorExitPlans: [],
  // msr 4.5b (CTX-04). Server-stamped and append-only; state-write keeps the
  // stored copy of each (shared/obligationEvaluations/cycle.cjs).
  // obligationEvaluations: { id, obligationId, outcome ('no-change-needed'|
  //   'change-needed'), note, evidenceRefs [{ type: 'document'|'artefact', id }],
  //   cycle, evaluatedAt, evaluatedBy, voidedAt?, voidedBy?, voidReason? } —
  //   obligation-evaluations-write
  // obligationCycleAnchors: { id, obligationId, firstEnabledAt, recordedAt } —
  //   obligations-enable, state-write
  obligationEvaluations: [],
  obligationCycleAnchors: [],

  // ── Audit Cycle Plans (ISO 27001 Clause 9.2, AFR A-03) ────
  // Multi-year plan guaranteeing every control group/topic is audited before
  // a certification cycle closes. Modelled as an array (not a singleton) so a
  // tenant is never forced through a 1-record migration if a second cycle is
  // ever needed, even though today's UI drives exactly one active plan.
  //
  // Only the ASSIGNMENT LIST is persisted — coverage %, per-year/per-quarter
  // effort (days), and load-bar percentages are always DERIVED at render time
  // from the assignments plus the scope units below, mirroring FIND-B-002's
  // "derive, never persist" discipline for this sibling module. No screen may
  // write a computed coverage/effort number back onto this record.
  //
  // scopeId basis (`basis` field), since APLA-03, resolves against the ONE
  // scope vocabulary the rest of Findings & Audits uses (auditScope.js):
  //   'topics'     -> buildTopicOptions(); scopeId is the bare topic id
  //                   ('grp-identity-access'), unchanged from before so every
  //                   assignment already in a production plan still resolves
  //                   with no migration.
  //   'frameworks' -> buildFrameworkCategoryOptions(state.frameworks), narrowed
  //                   by `basisFrameworkIds` (empty = every framework that has
  //                   categories); scopeId is namespaced
  //                   ('framework-category::fw-iso27002::A.5') and therefore
  //                   cannot collide with a bare topic id.
  // 'groups' is the pre-APLA-03 value and reads as 'topics': it pointed at the
  // baseline control-groups file, which is exactly what buildTopicOptions()
  // returns, so nothing a tenant sees changes. An assignment whose scopeId
  // resolves to nothing is rendered as an unresolved unit, never dropped —
  // a slot silently vanishing from a multi-year plan is the worst outcome
  // available here.
  //
  // `certificationId` (soft FK -> certifications[].id) anchors the cycle: when
  // set, `years` are the certificate's own issue-to-expiry years and the
  // coverage deadline is its recertification date. Null keeps the pre-APLA-03
  // typed-year behaviour, so a tenant with no certificate is never blocked
  // (FIND-B-006).
  //
  // `amendments[]` records every move of a planned slot:
  //   { id, at, by, scopeId, from: {year, quarter}, to: {year, quarter}, reason }
  // It is a log of what a HUMAN did, not a computed figure, which is why
  // persisting it does not cross FIND-B-005. It exists because PDCA's Check
  // phase compares actual against PREDICTED, and the original prediction has
  // to survive the plan being changed — the earliest amendment's `from` is
  // what makes the baseline derivable. ISO 27001 §9.2.2 wants the programme
  // and its changes retained as records regardless.
  //
  // Shape: { id, name, years: [2026, 2027, 2028],
  //   basis: 'topics'|'frameworks',   // 'groups' from before APLA-03 reads as 'topics'
  //   basisFrameworkIds: [],          // narrows the 'frameworks' basis; [] = all
  //   certificationId,                // soft FK -> certifications[].id, or null
  //   assignments: [{ id, scopeId, year, quarter: 1|2|3|4, auditId, addedAt,
  //     // auditId is a soft FK -> internalAudits[].id, set once the
  //     // scheduled slot has been executed.
  //     controlIds,        // APLA-04: present only on a SPLIT slot, naming the
  //                        // subset of its unit's controls this slot covers.
  //                        // Absent = covers the whole unit, the shape every
  //                        // assignment had before splitting existed. The
  //                        // subsets are disjoint, so a unit split across two
  //                        // quarters is still counted once in coverage.
  //     // The slot IS the audit's plan (APLA-04) — steps 1-3 of the internal
  //     // audit process, entered once here and inherited by the programme item
  //     // and the audit record rather than re-entered. All optional; a slot
  //     // with none of them is a valid, unplanned slot.
  //     objective,
  //     criteria: { documentRefs: [{ id, title, version }] },  // soft FKs -> documents[]
  //     leadAuditorId, participantIds: [], auditeeId,          // soft FKs -> users
  //     methods: [],       // 'interview'|'document-review'|'observation'
  //                        // |'sampling'|'walkthrough'|'re-performance'
  //     samplingNote, evidenceDueAt,
  //     estimatedDays,     // the planner's own estimate; blank derives days
  //                        // from the hours-per-control assumption instead.
  //                        // An entered estimate is an INPUT, never a stored
  //                        // computed total (FIND-B-005).
  //     // Materialisation links (APLA-05), both soft and both optional —
  //     // written when a human materialises the slot into the programme:
  //     programId,           // -> auditProgrammes[].id
  //     programAuditItemId }],  // -> auditProgrammes[].audits[].id
  //   // `auditId` above is finally WRITTEN by startAuditFromProgrammeItem()
  //   // once the materialised item is started. It was documented from the day
  //   // this key was added and no code ever set it, which is why the plan
  //   // could never show that one of its slots had actually been audited.
  //   amendments: [{ id, at, by, scopeId, from: {year, quarter},
  //     to: {year, quarter}, reason }],
  //   // APLA-08 — Act returns to Plan. What this cycle's COMPLETED audits
  //   // found is DERIVED into proposals on read (cycleProposals.js); nothing
  //   // is written and nothing counts until a human accepts one. Accepting
  //   // writes an ordinary assignment carrying `fromProposal` (the derived
  //   // id `<scopeId>::<kind>`) and `acceptedBy`. Declining requires a
  //   // reason — an unexplained "no" is not a record — and a dismissed
  //   // proposal is never re-proposed. Withdrawal (the basis disappeared)
  //   // records nothing, because no human decided anything.
  //   proposalDismissals: [{ id, scopeId, kind, reason,
  //     dismissedAt, dismissedBy }],
  //   createdAt, createdBy, updatedAt }
  auditCyclePlans: [],

  // Which cycle plan every Audits surface is looking at (GH #6449). Absent on
  // every tenant that has never used the plan selector, and absent is a valid
  // value: cyclePlanSelection.js falls back to the first plan, which is what
  // every reader used to hardcode. Nothing migrates, and a tenant already
  // holding a second plan that no screen could reach simply gains the ability
  // to select it.
  activeAuditCyclePlanId: null,

  // ── Certifications — RETIRED FROM THE PRODUCT ────────────
  // The certificate record and its external-visit calendar were removed from
  // Audits & Findings: the cycle plan now takes its years from the start year
  // the user types, and nothing in the module reads a certificate any more.
  //
  // The KEY IS DELIBERATELY KEPT. Tenants that used the feature still have
  // rows behind it, and state-write round-trips whatever it is given —
  // dropping the key here would orphan real tenant data on the next save for
  // no benefit. Nothing reads it, nothing writes it, and no migration touches
  // it. Leave it alone unless the data is being deliberately removed, which
  // is a destructive operation needing named-tenant confirmation.
  //
  // `auditCyclePlans[].certificationId` on an older plan is likewise inert:
  // it is neither read nor cleared, so an existing plan keeps working exactly
  // as it did.
  certifications: [],

  // Frameworks stored in state — metadata only; controls come from TCF index
  frameworks: [
    {
      id: 'fw-iso27001',
      type: 'standard',
      name: 'ISO 27001:2022',
      shortName: 'ISO 27001',
      abbrev: 'I27k1',
      description:
        'Information security management system requirements — 30 mandatory ISMS clauses across Clauses 4–10',
      color: '#60a5fa',
      version: '2022',
      enabled: true,
      sortOrder: 0,
    },
    {
      id: 'fw-iso27002',
      type: 'standard',
      name: 'ISO 27002:2022',
      shortName: 'ISO 27002',
      abbrev: 'I27k2',
      description:
        'Information security controls — 93 controls across 4 themes: Organizational, People, Physical and Technological',
      color: '#5eead4',
      version: '2022',
      enabled: true,
      sortOrder: 1,
    },
    {
      id: 'fw-cis18',
      type: 'standard',
      name: 'CIS Controls v8',
      shortName: 'CIS18',
      abbrev: 'CIS18',
      description: 'CIS Critical Security Controls v8 — 153 safeguards across 18 control groups',
      color: '#a78bfa',
      version: 'v8',
      enabled: true,
      sortOrder: 2,
    },
    {
      id: 'fw-nist-csf-20',
      type: 'standard',
      name: 'NIST Cybersecurity Framework 2.0',
      shortName: 'NIST CSF 2.0',
      abbrev: 'NIST',
      description:
        'NIST CSF 2.0 — 106 subcategory outcomes across 6 functions: Govern, Identify, Protect, Detect, Respond and Recover',
      color: '#4ade80',
      version: '2.0',
      enabled: false,
      sortOrder: 3,
    },
    {
      id: 'fw-nis2',
      type: 'law',
      name: 'NIS2 Directive',
      shortName: 'NIS2',
      abbrev: 'NIS2',
      description:
        'EU Network and Information Security Directive 2 — mandatory cybersecurity requirements for essential and important entities operating in critical sectors across the European Union.',
      color: '#3b82f6',
      version: '2022/2555',
      jurisdiction: 'EU',
      enabled: false,
      sortOrder: 4,
    },
    {
      id: 'fw-gdpr',
      type: 'law',
      name: 'General Data Protection Regulation',
      shortName: 'GDPR',
      abbrev: 'GDPR',
      description:
        'EU General Data Protection Regulation — comprehensive data protection and privacy framework applying to all organisations processing personal data of EU/EEA residents.',
      color: '#e879f9',
      version: '2016/679',
      jurisdiction: 'EU',
      enabled: false,
      sortOrder: 5,
    },
    {
      id: 'fw-pci-dss',
      type: 'standard',
      name: 'PCI DSS v4.0',
      shortName: 'PCI DSS',
      abbrev: 'PCIDSS',
      description:
        'Payment Card Industry Data Security Standard — security requirements for all entities that store, process, or transmit cardholder data and/or sensitive authentication data.',
      color: '#f59e0b',
      version: '4.0',
      enabled: false,
      sortOrder: 6,
    },
    {
      id: 'fw-gdpr-isae3000',
      type: 'standard',
      name: 'FSR ISAE 3000 — GDPR Processor Statement',
      shortName: 'FSR ISAE 3000',
      abbrev: 'FSR',
      description:
        'FSR – danske revisorer limited-assurance ISAE 3000 template for GDPR data-processor (Art. 28) statements — 45 control activities across 9 control objectives (Kontrolmål A–I).',
      color: '#a855f7',
      version: '2020',
      jurisdiction: 'DK',
      enabled: false,
      sortOrder: 7,
    },
    {
      id: 'fw-iso22301',
      type: 'standard',
      name: 'ISO 22301:2019',
      shortName: 'ISO 22301',
      abbrev: 'I22301',
      description:
        'Business continuity management system (BCMS) requirements \u2014 26 mandatory clauses across Clauses 4\u201310 covering context, leadership, planning, support, operation (including BIA, continuity strategies and exercise programme), performance evaluation and improvement.',
      color: '#10b981',
      version: '2019',
      enabled: false,
      sortOrder: 8,
    },
    {
      id: 'fw-iso42001',
      type: 'standard',
      name: 'ISO/IEC 42001:2023',
      shortName: 'ISO 42001',
      abbrev: 'I42k1',
      description:
        'Artificial Intelligence Management System — requirements for establishing, implementing, maintaining and continually improving an AI management system, including 27 main-clause requirements and 38 Annex A controls.',
      color: '#8b5cf6',
      version: '2023',
      enabled: false,
      sortOrder: 9,
    },
    {
      id: 'fw-eu-ai-act',
      type: 'law',
      name: 'EU AI Act',
      shortName: 'EU AI Act',
      abbrev: 'AIACT',
      description:
        'Regulation (EU) 2024/1689 — harmonised rules on artificial intelligence. Binding law covering prohibited practices, high-risk AI systems, transparency, general-purpose AI models, post-market monitoring and penalties.',
      color: '#0ea5e9',
      version: '2024/1689',
      jurisdiction: 'EU',
      enabled: false,
      sortOrder: 10,
    },
    {
      id: 'fw-dora',
      type: 'law',
      name: 'Digital Operational Resilience Act',
      shortName: 'DORA',
      abbrev: 'DORA',
      description:
        'EU Regulation (EU) 2022/2554 — directly applicable digital operational resilience requirements for financial entities and their ICT third-party service providers. Applies from 17 January 2025.',
      color: '#0ea5e9',
      version: '2022/2554',
      jurisdiction: 'EU',
      enabled: false,
      sortOrder: 11,
    },
    {
      id: 'fw-ski-0215',
      type: 'standard',
      name: 'SKI 02.15 Sikkerhedskrav',
      shortName: 'SKI 02.15',
      abbrev: 'SKI15',
      description:
        'Danish government supplier security requirements (Bilag B.3) for framework agreements 02.15 and 02.17 — 88 controls across 8 domains covering governance, personnel, asset protection, identity, vendor management, incidents, secure development and compliance.',
      color: '#c2410c',
      version: '1.0',
      jurisdiction: 'DK',
      enabled: false,
      sortOrder: 12,
    },
    {
      id: 'fw-cra',
      type: 'law',
      name: 'Cyber Resilience Act',
      shortName: 'CRA',
      abbrev: 'CRA',
      description:
        'EU Regulation (EU) 2024/2847 — horizontal cybersecurity requirements for products with digital elements (PDEs). Binds manufacturers, importers and distributors. Vulnerability and incident reporting applies from 11 September 2026; full applicability 11 December 2027. Per-tenant opt-in (CRA-B-001) — toggled by super-admin only.',
      color: '#06b6d4',
      version: '2024/2847',
      jurisdiction: 'EU',
      enabled: false,
      sortOrder: 13,
    },
  ],

  // ── Products with digital elements (PDEs) ─────────────────────────────
  // Skeleton — schema and boundaries only. No UI in this slice; the
  // module materialises once a CRA-enabled manufacturer tenant ships a
  // first PDE. See minimum-requirements/products.json (PROD-B-001..003)
  // and the CRA boundaries CRA-B-003 / CRA-B-004 that lock this in as
  // per-product (never org-level) data.
  products: [], // Product[] — see docs/state-schema.md

  // ── Vulnerability management (Defender integration) ──────────────────
  // Boundaries: VUL-B-001..010 in minimum-requirements.json.
  // Schema: docs/state-schema.md.
  msVulnerabilities: null, // raw Defender feed; system-write only (VUL-B-001)
  vulnerabilities: [], // derived per-asset findings (VUL-B-002)
  softwareInventory: [], // derived from CVE affectedSoftware
  appInventory: [], // broader software/SaaS map: Intune apps + Entra OAuth + MDCA shadow-IT
  vulnerabilityRules: [], // tenant rule store (VUL-B-010, versioned)
  vulnerabilityOverrides: [], // only human-write triage layer (VUL-B-003)
  ownedSoftwareRegistry: [], // internal/external classifier (VUL-B-007)

  // ── White-label branding ──────────────────────────────────────────────────
  branding: {
    companyName: '', // overrides the domain-derived product name (e.g. 'Tryggr') when set
    logoImage: '', // base64 data-URL for sidebar logo
    zoom: 1,
    panX: 0,
    panY: 0,
  },

  // Tenant-managed document content templates. Built-in templates live in
  // src/constants/privacyDocTemplates.js (GDPR set) and are merged with
  // this array at picker time. Each entry: { id, name, description,
  // category, tags, content (HTML), createdAt, createdBy, updatedAt }.
  // Picker applies substituteTemplateVars() on selection so {{tenant_name}},
  // {{author_name}}, {{owner_name}}, {{today}} etc. are expanded into the
  // editor at template-apply time (one-shot, not stored back). Phase 5a.
  documentTemplates: [],

  // ── AI Governance ─────────────────────────────────────────────────────────
  // Authoritative data lives in the ai_state SQL table (separate from
  // tenant_state). state-read overlays these keys; state-write strips them.
  // The write path is ai-governance-write (plan-gated: 'ai-governance').
  // Boundaries: see minimum-requirements/aiGovernance.json.
  aiSystems: [], // AiSystem[] — registered AI tools and use cases
  aiRiskProfileOverrides: [], // AiRiskProfileOverride[] — tenant deltas on built-in profiles
  aiProhibitedScreenings: [], // AiProhibitedScreening[] — Art. 5 attestation records
  tias: [], // Tia[] — Transfer Impact Assessments for AI systems
  aiScores: {}, // { [aiSystemId]: AiMaturityScore } — computed by sweeper
  aiSettings: {}, // { allowTiaAuthorApproval?: boolean } — absent/false: a TIA's author cannot approve it (GH #8975)
};

export { INITIAL_STATE };
